import { Container, Graphics, Text, TextStyle } from "pixi.js";
import type { SongBoundary } from "@/lib/studio/view/sequence-layout";

/**
 * Les frontières entre les morceaux d'une séquence, sur le rouleau.
 *
 * Une ligne en travers là où un morceau commence, et son nom écrit dessus —
 * « Prise 3 » sur un enregistrement de récital, le titre sur un medley. La
 * ligne défile avec les notes, comme les lignes de mesure ; l'étiquette est
 * accrochée à elle et part avec elle : ce qu'elle nomme est ce qui suit, et
 * une fois passée la tête, le nom du morceau courant se lit dans la barre
 * latérale, pas sur le rouleau.
 *
 * # Ce qu'elle n'est pas
 *
 * Ni une ligne de mesure — plus franche qu'une barre, parce qu'une frontière
 * est un repère qu'on cherche, pas une trame qu'on suit —, ni une borne de
 * passage travaillé (`WorkRange`), qui règle deux bords et voile l'extérieur :
 * ici rien n'est voilé, les deux morceaux se valent. Achromatique, pour la
 * même raison que ces deux-là : les mains tiennent les seules teintes de la
 * surface, et les accords leurs couleurs de fondamentale.
 *
 * # Où le nom s'écrit
 *
 * Au centre. La gauche appartient aux noms d'accords et à la règle graduée,
 * la droite à la marge de tonalité ; au milieu tombent les notes, mais une
 * frontière est le seul instant où il n'y en a pas — c'est le silence entre
 * deux prises. Sans cartouche derrière, avec une ombre portée, comme les
 * noms d'accords : un aplat cacherait ce que le rouleau existe pour montrer.
 *
 * Rien n'est dessiné sans séquence : la liste est vide, et le calque ne
 * coûte qu'un test de longueur par image.
 */

type SongBoundariesProps = {
  readonly boundaries: readonly SongBoundary[];
  /** Change avec la liste : c'est ce que la détection de changement compare. */
  readonly version: number;
  readonly fromTick: number;
  readonly toTick: number;
  readonly width: number;
  readonly height: number;
};

type BoundarySlot = {
  line: Graphics;
  /** Largeur cuite dans la ligne ; -1 tant qu'elle n'est pas dessinée. */
  lineWidth: number;
  text: Text;
};

/**
 * Contraste de la ligne sur le fond du rouleau (#0a0a10) : 0,5 la met vers
 * 4,8:1, au-dessus d'une barre de mesure (0,4) et sous les bords d'un passage
 * travaillé (0,62), qui sont ce que le joueur doit trouver, quand ils sont là.
 */
const LINE_ALPHA = 0.5;
const LINE_WIDTH = 2;
/** L'étiquette, posée juste au-dessus de sa ligne. */
const LABEL_ABOVE = 4;
const LABEL_ALPHA = 0.9;

export class SongBoundaries {
  private container: Container;
  private pool: BoundarySlot[] = [];
  private poolIndex = 0;
  private previousProps?: SongBoundariesProps;

  constructor(stage: Container) {
    this.container = new Container();
    stage.addChild(this.container);
  }

  destroy(): void {
    for (const slot of this.pool) {
      slot.line.destroy();
      slot.text.destroy();
    }

    this.pool.length = 0;
    this.poolIndex = 0;
    this.container.destroy({ children: true });
  }

  draw(props: SongBoundariesProps): void {
    if (props.boundaries.length === 0) {
      if (this.poolIndex > 0) {
        this.resetPool();
      }

      this.previousProps = props;

      return;
    }

    if (this.shouldSkip(props)) {
      return;
    }

    this.previousProps = props;

    const { boundaries, fromTick, toTick, width, height } = props;
    const span = toTick - fromTick;

    this.resetPool();

    if (span <= 0 || height <= 0) {
      return;
    }

    const pxPerTick = height / span;

    for (const boundary of boundaries) {
      if (boundary.tick < fromTick || boundary.tick > toTick) {
        continue;
      }

      const y = height - (boundary.tick - fromTick) * pxPerTick;
      const slot = this.acquire();

      if (slot.lineWidth !== width) {
        slot.line.clear();
        slot.line.moveTo(0, 0);
        slot.line.lineTo(width, 0);
        slot.line.stroke({ color: 0xffffff, alpha: LINE_ALPHA, width: LINE_WIDTH });
        slot.lineWidth = width;
      }

      slot.line.y = y;
      slot.line.visible = true;

      if (slot.text.text !== boundary.label) {
        slot.text.text = boundary.label;
      }

      slot.text.x = width / 2;
      slot.text.y = y - LABEL_ABOVE;
      slot.text.alpha = LABEL_ALPHA;
      slot.text.visible = true;
    }
  }

  private shouldSkip(props: SongBoundariesProps): boolean {
    const prev = this.previousProps;

    if (!prev) {
      return false;
    }

    return prev.version === props.version
      && prev.fromTick === props.fromTick
      && prev.toTick === props.toTick
      && prev.width === props.width
      && prev.height === props.height;
  }

  private resetPool(): void {
    for (let i = 0; i < this.poolIndex; i++) {
      this.pool[i].line.visible = false;
      this.pool[i].text.visible = false;
    }

    this.poolIndex = 0;
  }

  private acquire(): BoundarySlot {
    if (this.poolIndex < this.pool.length) {
      return this.pool[this.poolIndex++];
    }

    const line = new Graphics();
    const text = new Text({
      text: "",
      style: new TextStyle({
        fontSize: 13,
        fontWeight: "600",
        fontFamily: "Inter, sans-serif",
        fill: 0xffffff,
        dropShadow: {
          color: 0x000000,
          blur: 6,
          distance: 0,
          alpha: 0.8,
        },
      }),
    });

    // Centrée sur la ligne, posée dessus : l'ancre bas-centre fait les deux.
    text.anchor.set(0.5, 1);
    line.visible = false;
    text.visible = false;

    this.container.addChild(line);
    this.container.addChild(text);

    const slot: BoundarySlot = { line, lineWidth: -1, text };
    this.pool.push(slot);
    this.poolIndex++;

    return slot;
  }
}
